class VimeoPlayerControls extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
    this.userHasInteracted = false
    this.initializePlayer()
  }

  disconnectedCallback () {
    if (this.observer) this.observer.disconnect()
    super.disconnectedCallback?.()
  }

  async waitForVimeo (attempts = 3, interval = 500) {
    while (attempts-- > 0) {
      if (window.Vimeo) return window.Vimeo
      await new Promise((resolve) => setTimeout(resolve, interval))
    }
    throw new Error('Vimeo SDK not available')
  }

  async initializePlayer () {
    const Vimeo = await this.waitForVimeo()

    const iframe = this.els.iframe?.element || this.querySelector('[data-el="iframe"]') || this.querySelector('iframe')
    if (!iframe) return

    this.player = new Vimeo.Player(iframe)

    this.playButton = this.els.playButton?.element || this.querySelector('[data-el="playButton"]')
    this.soundButton = this.els.soundButton?.element || this.querySelector('[data-el="soundButton"]')

    this.playIcon = this.playButton?.querySelector('img')
    this.soundIcon = this.soundButton?.querySelector('img')

    this.player.ready().then(() => {
      this.setupIntersectionObserver()
      this.setupListeners()
      this.syncPlaybackWithVisibility()
    })
  }

  playVideo () {
    this.player.play().catch((error) => {
      // eslint-disable-next-line no-console
      console.log('Vimeo play blocked:', error)
    })
    if (this.playIcon) {
      this.playIcon.src = this.playButton.dataset.iconPause
      this.playIcon.alt = 'Pause'
    }
  }

  pauseVideo () {
    this.player.pause().catch(() => {})
    if (this.playIcon) {
      this.playIcon.src = this.playButton.dataset.iconPlay
      this.playIcon.alt = 'Play'
    }
  }

  isInViewport () {
    const rect = this.getBoundingClientRect()
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth

    if (rect.width <= 0 || rect.height <= 0) return false

    const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)
    const visibleWidth = Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0)
    if (visibleHeight <= 0 || visibleWidth <= 0) return false

    const visibleRatio = (visibleHeight * visibleWidth) / (rect.height * rect.width)
    return visibleRatio >= 0.2
  }

  syncPlaybackWithVisibility () {
    if (this.userHasInteracted) return
    if (this.isInViewport()) {
      this.playVideo()
    } else {
      this.pauseVideo()
    }
  }

  muteVideo () {
    this.player.setMuted(true)
    if (this.soundIcon) {
      this.soundIcon.src = this.soundButton.dataset.iconSoundOff
      this.soundIcon.alt = 'Unmute'
    }
  }

  unmuteVideo () {
    this.player.setMuted(false)
    this.player.setVolume(1)
    if (this.soundIcon) {
      this.soundIcon.src = this.soundButton.dataset.iconSoundOn
      this.soundIcon.alt = 'Mute'
    }
  }

  setupIntersectionObserver () {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (this.userHasInteracted) return
        if (entry.isIntersecting) {
          this.playVideo()
        } else {
          this.pauseVideo()
        }
      })
    }, { threshold: 0.2 })

    this.observer.observe(this)
  }

  setupListeners () {
    super.setupListeners()

    if (this.playButton && this.playIcon) {
      this.listeners.add(this.playButton, 'click', () => {
        this.userHasInteracted = true
        this.player.getPaused().then((paused) => {
          if (paused) {
            this.playVideo()
          } else {
            this.pauseVideo()
          }
        })
      })
    }

    if (this.soundButton && this.soundIcon) {
      this.listeners.add(this.soundButton, 'click', () => {
        this.player.getMuted().then((muted) => {
          if (muted) {
            this.unmuteVideo()
          } else {
            this.muteVideo()
          }
        })
      })
    }
  }
}

if (!customElements.get('vimeo-player-controls')) {
  customElements.define('vimeo-player-controls', VimeoPlayerControls)
}
